/**
 * featurebase/ConsentDialog.tsx
 * Consent dialog for Featurebase third-party data sharing.
 *
 * Reusable by both SupportButton (Phase 2) and the Settings toggle (Phase 3).
 * Soft-close (backdrop click / Escape) does NOT call denyConsent() —
 * only the explicit "Decline" button changes consent state.
 */

// 1. External dependencies
import { ShieldCheck } from 'lucide-react';

// 2. Workspace packages
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@enfomentis/ui';

// 3. Relative imports
import { useFeaturebase } from './useFeaturebase';

export interface ConsentDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ConsentDialog — Explains third-party data sharing and collects consent.
 *
 * - "Accept" → grantConsent() + close
 * - "Decline" → denyConsent() + close
 * - Dismiss (backdrop / Escape) → close only (no consent change)
 */
export function ConsentDialog({ isOpen, onOpenChange }: ConsentDialogProps) {
  const { grantConsent, denyConsent } = useFeaturebase();

  const handleAccept = () => {
    grantConsent();
    onOpenChange(false);
  };

  const handleDecline = () => {
    denyConsent();
    onOpenChange(false);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} isDismissable placement="center">
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <span>Enable Feedback &amp; Support?</span>
        </ModalHeader>

        <ModalBody className="space-y-3">
          <p className="text-sm">
            This feature connects you to our support team through Featurebase, a
            third-party platform. To set up your support channel, your{' '}
            <strong>name</strong> and <strong>email address</strong> will be
            shared with Featurebase.
          </p>
          <p className="text-sm text-default-500">
            Your trading data is never sent to Featurebase.
          </p>
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={handleDecline}>
            Decline
          </Button>
          <Button color="primary" variant="solid" onPress={handleAccept}>
            Accept
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
